import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import mbLogo from '../assets/milwaukee-ballet-logo.png';
import agmaMark from '../assets/agma-mark.png';
import { apiFetch } from '../utils/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className='login-page'>
        <div className='login-card'>
          <div className='login-brand'>
            <img src={mbLogo} alt='Milwaukee Ballet' className='login-logo' />
            <h1 className='login-title'>PROSCENO</h1>
          </div>
          <p className='login-error' style={{ marginTop: 16 }}>
            Invalid reset link. Please request a new one.
          </p>
          <Link to='/forgot-password' className='forgot-pw-back-link' style={{ display: 'block', marginTop: 12 }}>
            Request a reset link
          </Link>
        </div>
        <div className='login-footer'>
          <img src={agmaMark} alt='AGMA' className='login-footer-mark' />
          <span>
            This system implements the photo approval rights guaranteed under the
            AGMA Collective Bargaining Agreement, Section 4, Article 41.
          </span>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json() as { message?: string };
      if (!res.ok) {
        setError(data.message || 'Failed to reset password.');
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='login-page'>
      <div className='login-card'>
        <div className='login-brand'>
          <img src={mbLogo} alt='Milwaukee Ballet' className='login-logo' />
          <h1 className='login-title'>PROSCENO</h1>
          <p className='login-subtitle'>Set a new password</p>
        </div>

        {success ? (
          <div className='forgot-pw-success'>
            <p>Password updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className='user-form-field'>
              <label htmlFor='new-password'>New password</label>
              <div className='password-field-wrapper'>
                <input
                  id='new-password'
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  autoComplete='new-password'
                  minLength={8}
                />
                <button
                  type='button'
                  className='password-toggle'
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'/>
                      <line x1='1' y1='1' x2='23' y2='23'/>
                    </svg>
                  ) : (
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/>
                      <circle cx='12' cy='12' r='3'/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className='user-form-field' style={{ marginTop: 16 }}>
              <label htmlFor='confirm-password'>Confirm new password</label>
              <input
                id='confirm-password'
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete='new-password'
              />
            </div>
            {error && <p className='login-error'>{error}</p>}
            <button type='submit' className='login-submit-btn' disabled={loading}>
              {loading ? 'Saving…' : 'Set new password'}
            </button>
          </form>
        )}
      </div>

      <div className='login-footer'>
        <img src={agmaMark} alt='AGMA' className='login-footer-mark' />
        <span>
          This system implements the photo approval rights guaranteed under the
          AGMA Collective Bargaining Agreement, Section 4, Article 41.
        </span>
      </div>
    </div>
  );
}
